import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LogOut,
  Menu,
  MessageCircle,
  Users,
  ChevronDown,
  LayoutDashboard,
  Smartphone,
  Plus,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { useToast } from "./ui/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { apiService } from "../services/api";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: user } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const response = await apiService.getCurrentUser();
      return response.data;
    },
  });

  const handleLogout = () => {
    apiService.logout();
    queryClient.clear();
    navigate("/login");
    toast({
      title: t("common.logout_success"),
      description: t("common.logout_success_description"),
    });
  };

  const isActive = (path: string) =>
    location.pathname === path
      ? "bg-accent text-accent-foreground"
      : "hover:bg-accent/50 transition-colors";

  const NavLinks = () => (
    <>
      <Link
        to="/dashboard"
        className={`px-3 py-2 rounded-md text-sm font-medium ${isActive(
          "/dashboard"
        )}`}
        onClick={() => setIsOpen(false)}
      >
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <LayoutDashboard className="h-4 w-4" />
          <span>{t("pages.dashboard.nav")}</span>
        </div>
      </Link>
      <Link
        to="/messages"
        className={`px-3 py-2 rounded-md text-sm font-medium ${isActive(
          "/messages"
        )}`}
        onClick={() => setIsOpen(false)}
      >
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <MessageCircle className="h-4 w-4" />
          <span>{t("pages.messages.nav")}</span>
        </div>
      </Link>
      <Link
        to="/accounts"
        className={`px-3 py-2 rounded-md text-sm font-medium ${isActive(
          "/accounts"
        )}`}
        onClick={() => setIsOpen(false)}
      >
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <Smartphone className="h-4 w-4" />
          <span>{t("pages.accounts.nav")}</span>
        </div>
      </Link>
      {user?.admin && (
        <>
          <Link
            to="/admin/dashboard"
            className={`px-3 py-2 rounded-md text-sm font-medium ${isActive(
              "/admin/dashboard"
            )}`}
            onClick={() => setIsOpen(false)}
          >
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <LayoutDashboard className="h-4 w-4" />
              <span>{t("pages.admin.dashboard.nav")}</span>
            </div>
          </Link>
          <Link
            to="/admin/users"
            className={`px-3 py-2 rounded-md text-sm font-medium ${isActive(
              "/admin/users"
            )}`}
            onClick={() => setIsOpen(false)}
          >
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <Users className="h-4 w-4" />
              <span>{t("pages.admin.users.nav")}</span>
            </div>
          </Link>
        </>
      )}
    </>
  );

  const UserMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center space-x-2 rtl:space-x-reverse"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage
              src={`https://api.dicebear.com/7.x/initials/svg?seed=${
                user?.name || user?.email
              }`}
            />
            <AvatarFallback>
              {(user?.name || user?.email)?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="hidden md:inline-block">
            {user?.name || user?.email}
          </span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/settings" className="w-full">
            <Settings className="h-4 w-4 mr-2 rtl:ml-2 rtl:mr-0" />
            {t("pages.settings.nav")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout} className="text-destructive">
          <LogOut className="h-4 w-4 mr-2 rtl:ml-2 rtl:mr-0" />
          {t("common.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <nav className="border-b mb-8 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center h-16 justify-between">
          <div className="flex items-center md:space-x-8">
            <Link
              to="/dashboard"
              className="font-bold text-xl ml-8 rtl:mr-8 rtl:ml-0 flex items-center  transition-transform transform hover:scale-125 duration-400"
            >
              {t("common.app_name")}
              <Plus className="h-5 w-5 mx-1 text-primary" />
            </Link>

            {/* Desktop menu */}
            <div className="hidden md:flex items-center space-x-4 rtl:space-x-reverse">
              <NavLinks />
            </div>
          </div>

          {/* User menu - desktop only */}
          {user && (
            <div className="hidden md:flex items-center space-x-2">
              <LanguageSwitcher />
              <UserMenu />
            </div>
          )}

          {/* Mobile menu */}
          <div className="md:hidden flex items-center">
            <LanguageSwitcher />
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[240px] sm:w-[280px]">
                <SheetHeader>
                  <SheetTitle>{t("common.menu")}</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col space-y-4 mt-4">
                  {user && (
                    <div className="px-3 py-2">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse mb-4">
                        <Avatar className="h-8 w-8">
                          <AvatarImage
                            src={`https://api.dicebear.com/7.x/initials/svg?seed=${
                              user?.name || user?.email
                            }`}
                          />
                          <AvatarFallback>
                            {(user?.name || user?.email)
                              ?.charAt(0)
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span>{user?.name || user?.email}</span>
                      </div>
                    </div>
                  )}
                  <NavLinks />
                  {user && (
                    <>
                      <div className="border-t border-gray-200 my-4"></div>
                      <Link
                        to="/settings"
                        className={`px-3 py-2 rounded-md text-sm font-medium ${isActive(
                          "/settings"
                        )}`}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <Settings className="h-4 w-4" />
                          <span>{t("pages.settings.nav")}</span>
                        </div>
                      </Link>
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          handleLogout();
                        }}
                        className="px-3 py-2 rounded-md text-sm font-medium text-destructive hover:bg-accent/50 transition-colors w-full text-start"
                      >
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <LogOut className="h-4 w-4" />
                          <span>{t("common.logout")}</span>
                        </div>
                      </button>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
